import {
  BookmarkIcon,
  CircularProgress,
  IconButton,
  SvgIconProps,
  Tooltip,
  Typography,
} from '@freighthub/silhouette-core'
import { FC, MouseEvent, useState } from 'react'
import { useI18n } from 'utils/localisation'

import { useShipmentBookmark } from '../service/useShipmentBookmark'

type ShipmentBookmarkProps = {
  shipmentId: string
  iconSize?: SvgIconProps['fontSize']
}

export const translationKeys = {
  bookmarkShipment: 'shipment_bookmark',
  removeBookmarkShipment: 'shipment_remove-bookmark',
  additionalTextBookmarkShipment: 'shipment_bookmark-additional-text',
}

export const testIds = {
  bookmark: 'shipment-bookmark',
  bookmarkTitle: 'shipment-bookmark-title',
  bookmarkAdditionalTitle: 'shipment-bookmark-additional-title',
}

const trackingIds = {
  bookmark: 'shipments-bookmark',
}

export const ShipmentBookmark: FC<ShipmentBookmarkProps> = ({ shipmentId, iconSize = 'large' }) => {
  const { translate } = useI18n()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { addBookmark, removeBookmark, bookmarkedShipmentIds } = useShipmentBookmark()

  const isBookmarked: boolean = bookmarkedShipmentIds?.includes(shipmentId)

  const handleBookmarkRequest = async (event: MouseEvent) => {
    event.preventDefault()
    setIsLoading(true)
    if (isBookmarked) await removeBookmark(shipmentId)
    else await addBookmark(shipmentId)
    setIsLoading(false)
  }

  const iconSizeMap: Partial<Record<NonNullable<SvgIconProps['fontSize']>, number>> = {
    large: 41,
    medium: 30,
  }

  if (isLoading) return <CircularProgress size={iconSizeMap[iconSize]} color="inherit" />
  return (
    <Tooltip
      title={
        <>
          <Typography
            variant="body3-medium"
            data-testid={testIds.bookmarkTitle}
            component="h6"
            color="inherit"
            sx={(theme) => ({ marginBottom: theme.spacing(0.25) })}
          >
            {translate(isBookmarked ? translationKeys.removeBookmarkShipment : translationKeys.bookmarkShipment)}
          </Typography>
          <Typography variant="body3" data-testid={testIds.bookmarkAdditionalTitle} color="inherit">
            {translate(translationKeys.additionalTextBookmarkShipment)}
          </Typography>
        </>
      }
    >
      <IconButton
        data-testid={testIds.bookmark}
        data-tracking-id={trackingIds.bookmark}
        onClick={handleBookmarkRequest}
        color="inherit"
        size="small"
      >
        <BookmarkIcon fontSize={iconSize} color={isBookmarked ? 'primary' : 'inherit'} />
      </IconButton>
    </Tooltip>
  )
}
